import { useCallback, useEffect, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { router } from 'expo-router';
import { observer } from 'mobx-react';

import DefaultBackground from '@/components/DefaultBackground';
import TopbarLogo from '@/components/TopbarLogo';
import { ThemedText } from '@/components/ThemedText';
import { ThemedView } from '@/components/ThemedView';
import { useScale } from '@/hooks/useScale';
import { useStores } from '@/stores/StoreProvider';
import {
  clearAllCachedVideos,
  fillRemainingSpace,
  getCachedVideosSize,
  getFreeDiskSpace,
  getTotalDiskSpace,
  removeFillerFile,
} from '@/services/videoDownload';
import { formatBytes } from '@/utils/formatBytes';

type DiskStats = {
  free: number | null;
  total: number | null;
  cached: number;
};

function FileSystemDebugScreen() {
  const styles = useStyles();
  const { guidedWorkoutPlayerStore } = useStores();

  const [stats, setStats] = useState<DiskStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [fillProgress, setFillProgress] = useState<number | null>(null);
  const [isRemovingFiller, setIsRemovingFiller] = useState(false);

  const busy = isDeleting || fillProgress !== null || isRemovingFiller;

  const loadStats = useCallback(async () => {
    setIsLoading(true);
    try {
      const [free, total, cached] = await Promise.all([
        getFreeDiskSpace(),
        getTotalDiskSpace(),
        getCachedVideosSize(),
      ]);
      setStats({ free, total, cached });
    } catch (err) {
      console.warn('[FileSystemDebug] failed to load disk stats', err);
      setStats({ free: null, total: null, cached: 0 });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const notify = (title: string, message: string) => {
    // react-native-web's Alert.alert is unreliable; use window.alert on web.
    if (Platform.OS === 'web') {
      if (typeof window !== 'undefined') {
        window.alert(`${title}\n\n${message}`);
      }
      return;
    }
    Alert.alert(title, message);
  };

  const performDeleteVideos = async () => {
    console.log('[FileSystemDebug] Delete all videos requested');
    setIsDeleting(true);
    try {
      guidedWorkoutPlayerStore.clearVideoCacheState();
      await clearAllCachedVideos();
      console.log('[FileSystemDebug] Delete all videos: done');
    } catch (err) {
      console.warn('[FileSystemDebug] Failed to delete videos:', err);
      Alert.alert(
        'Could not delete videos',
        'Something went wrong while clearing the video cache.',
      );
    } finally {
      setIsDeleting(false);
      await loadStats();
    }
  };

  const confirmDeleteVideos = () => {
    const title = 'Delete all videos?';
    const message =
      "You can re-download any workout. This won't affect your progress.";
    // react-native-web's Alert.alert drops button callbacks, so use
    // window.confirm on web (matching the settings screen).
    if (Platform.OS === 'web') {
      if (
        typeof window !== 'undefined' &&
        window.confirm(`${title}\n\n${message}`)
      ) {
        performDeleteVideos();
      }
      return;
    }
    Alert.alert(title, message, [
      { text: 'Cancel', style: 'cancel' },
      { text: 'Delete', style: 'destructive', onPress: performDeleteVideos },
    ]);
  };

  const fillDisk = async (outsideVideosDir: boolean) => {
    console.log(
      `[FileSystemDebug] Fill remaining space requested (outside=${outsideVideosDir})`,
    );
    setFillProgress(0);
    try {
      await fillRemainingSpace({
        outsideVideosDir,
        onProgress: (written, target) => {
          const pct = target > 0 ? Math.round((written / target) * 100) : 100;
          setFillProgress(pct);
        },
      });
    } catch (err) {
      console.warn('[FileSystemDebug] Fill failed:', err);
      Alert.alert(
        'Could not fill disk',
        err instanceof Error ? err.message : 'Something went wrong.',
      );
    } finally {
      setFillProgress(null);
      await loadStats();
    }
  };

  const removeFiller = async () => {
    console.log('[FileSystemDebug] Remove filler file requested');
    const freeBefore = stats?.free ?? null;
    setIsRemovingFiller(true);
    try {
      await removeFillerFile();
      const freeAfter = await getFreeDiskSpace();
      const freed =
        freeBefore != null && freeAfter != null
          ? Math.max(0, freeAfter - freeBefore)
          : null;
      notify(
        'Filler removed',
        freed != null
          ? `Freed ${formatBytes(freed)}.`
          : 'The filler file has been deleted.',
      );
    } catch (err) {
      console.warn('[FileSystemDebug] Remove filler failed:', err);
      notify(
        'Could not remove filler',
        err instanceof Error ? err.message : 'Something went wrong.',
      );
    } finally {
      setIsRemovingFiller(false);
      await loadStats();
    }
  };

  const formatStat = (value: number | null): string =>
    value == null ? 'Unavailable' : formatBytes(value);

  return (
    <ThemedView style={styles.container}>
      <DefaultBackground />

      {/* Top bar */}
      <View style={styles.topBar}>
        <Pressable
          style={({ focused }) => [
            styles.backButton,
            focused && styles.backButtonFocused,
          ]}
          onPress={() => router.back()}
        >
          <ThemedText style={styles.backButtonText}>Back</ThemedText>
        </Pressable>
        <View style={styles.topBarSpacer} />
        <TopbarLogo />
        <View style={styles.topBarSpacer} />
        <View style={styles.backButtonPlaceholder} />
      </View>

      <ThemedText style={styles.title}>File System</ThemedText>

      <ScrollView
        scrollEventThrottle={16}
        contentContainerStyle={styles.scrollContent}
      >
        <View style={styles.section}>
          <ThemedText style={styles.sectionTitle}>Disk Space</ThemedText>
          {isLoading || !stats ? (
            <ActivityIndicator color="#fff" />
          ) : (
            <>
              <View style={styles.statRow}>
                <ThemedText style={styles.statLabel}>Available</ThemedText>
                <ThemedText style={styles.statValue}>
                  {formatStat(stats.free)}
                </ThemedText>
              </View>
              <View style={styles.statRow}>
                <ThemedText style={styles.statLabel}>Total</ThemedText>
                <ThemedText style={styles.statValue}>
                  {formatStat(stats.total)}
                </ThemedText>
              </View>
              <View style={styles.statRow}>
                <ThemedText style={styles.statLabel}>Used by videos</ThemedText>
                <ThemedText style={styles.statValue}>
                  {formatBytes(stats.cached)}
                </ThemedText>
              </View>
              {stats.free == null && (
                <ThemedText style={styles.mutedText}>
                  Disk capacity isn&apos;t reported on this platform (web).
                </ThemedText>
              )}
            </>
          )}
          <Pressable
            disabled={isLoading || busy}
            onPress={loadStats}
            style={({ focused }) => [
              styles.button,
              focused && styles.buttonFocused,
              (isLoading || busy) && styles.buttonDisabled,
            ]}
          >
            <ThemedText style={styles.buttonText}>Refresh</ThemedText>
          </Pressable>
        </View>

        <View style={styles.section}>
          <ThemedText style={styles.sectionTitle}>Videos</ThemedText>
          <Pressable
            disabled={busy}
            onPress={confirmDeleteVideos}
            style={({ focused }) => [
              styles.button,
              focused && styles.buttonFocused,
              busy && styles.buttonDisabled,
            ]}
          >
            {isDeleting ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <ThemedText style={styles.buttonText}>
                Delete all videos
                {stats ? ` (${formatBytes(stats.cached)})` : ''}
              </ThemedText>
            )}
          </Pressable>
        </View>

        {stats?.free != null && (
          <View style={styles.section}>
            <ThemedText style={styles.sectionTitle}>Testing</ThemedText>
            <ThemedText style={styles.mutedText}>
              Writes a dummy file to simulate a full disk (leaves ~10 MB free).
            </ThemedText>
            <ThemedText style={styles.mutedText}>
              • In videos cache: removed by eviction, so a download recovers and
              succeeds.
            </ThemedText>
            <Pressable
              disabled={busy}
              onPress={() => fillDisk(false)}
              style={({ focused }) => [
                styles.button,
                focused && styles.buttonFocused,
                busy && styles.buttonDisabled,
              ]}
            >
              {fillProgress !== null ? (
                <ThemedText style={styles.buttonText}>
                  Filling… {fillProgress}%
                </ThemedText>
              ) : (
                <ThemedText style={styles.buttonText}>
                  Fill in videos cache
                </ThemedText>
              )}
            </Pressable>
            <ThemedText style={styles.mutedText}>
              • Outside videos cache: eviction can&apos;t reclaim it, so a
              download hits the storage-full alert.
            </ThemedText>
            <Pressable
              disabled={busy}
              onPress={() => fillDisk(true)}
              style={({ focused }) => [
                styles.button,
                focused && styles.buttonFocused,
                busy && styles.buttonDisabled,
              ]}
            >
              {fillProgress !== null ? (
                <ThemedText style={styles.buttonText}>
                  Filling… {fillProgress}%
                </ThemedText>
              ) : (
                <ThemedText style={styles.buttonText}>
                  Fill outside videos cache
                </ThemedText>
              )}
            </Pressable>
            <Pressable
              disabled={busy}
              onPress={removeFiller}
              style={({ focused }) => [
                styles.button,
                focused && styles.buttonFocused,
                busy && styles.buttonDisabled,
              ]}
            >
              {isRemovingFiller ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <ThemedText style={styles.buttonText}>
                  Remove filler file
                </ThemedText>
              )}
            </Pressable>
          </View>
        )}
      </ScrollView>
    </ThemedView>
  );
}

const useStyles = function () {
  const scale = useScale();
  return StyleSheet.create({
    container: {
      flex: 1,
    },
    topBar: {
      flexDirection: 'row',
      justifyContent: 'center',
      alignItems: 'center',
      paddingVertical: 24 * scale,
      paddingHorizontal: 24 * scale,
    },
    topBarSpacer: {
      flex: 1,
    },
    backButton: {
      paddingVertical: 8 * scale,
      paddingHorizontal: 20 * scale,
      backgroundColor: 'rgba(255,255,255,0.15)',
      borderRadius: 20 * scale,
      borderWidth: 2,
      borderColor: 'transparent',
    },
    backButtonFocused: {
      backgroundColor: 'rgba(255,255,255,0.3)',
      borderColor: '#fff',
    },
    backButtonPlaceholder: {
      width: 80 * scale,
    },
    backButtonText: {
      color: '#fff',
      fontSize: 14 * scale,
      fontWeight: '600',
    },
    title: {
      color: '#fff',
      fontSize: 28 * scale,
      fontWeight: 'bold',
      textAlign: 'center',
      marginBottom: 16 * scale,
    },
    scrollContent: {
      paddingHorizontal: 40 * scale,
      paddingBottom: 40 * scale,
      gap: 16 * scale,
    },
    section: {
      backgroundColor: 'rgba(255,255,255,0.1)',
      borderRadius: 12 * scale,
      padding: 20 * scale,
      gap: 10 * scale,
    },
    sectionTitle: {
      color: '#fff',
      fontSize: 18 * scale,
      fontWeight: '700',
    },
    statRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    statLabel: {
      color: 'rgba(255,255,255,0.7)',
      fontSize: 15 * scale,
    },
    statValue: {
      color: '#fff',
      fontSize: 15 * scale,
      fontWeight: '600',
    },
    mutedText: {
      color: 'rgba(255,255,255,0.7)',
    },
    button: {
      paddingVertical: 10 * scale,
      paddingHorizontal: 18 * scale,
      borderRadius: 10 * scale,
      borderWidth: 2,
      borderColor: 'transparent',
      alignSelf: 'flex-start',
      backgroundColor: 'rgba(255,255,255,0.15)',
    },
    buttonFocused: {
      backgroundColor: 'rgba(255,255,255,0.3)',
      borderColor: '#fff',
    },
    buttonText: {
      color: '#fff',
      fontSize: 14 * scale,
      fontWeight: '600',
    },
    buttonDisabled: {
      opacity: 0.4,
    },
  });
};

export default observer(FileSystemDebugScreen);
